import { useState, useEffect, useCallback } from 'react'
import { message } from 'antd'

export const useCaseTags = ({ caseId, apiClasses, onTagsUpdated }) => {
  const [availableTags, setAvailableTags] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchAvailableTags = useCallback(async () => {
    if (!apiClasses?.CaseTagsAPI) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await apiClasses.CaseTagsAPI.getCaseTags()
      const { data } = response

      if (data && !data.error) {
        const { caseTags } = data
        const mutableTags = caseTags
          .filter(tag => !tag.immutable && tag[0] !== '*')
          .map(tag => tag.tag || tag)
        setAvailableTags(mutableTags)
      } else {
        message.warning('Unable to retrieve case tags')
        setAvailableTags(['urgent', 'follow-up', 'reviewed', 'needs-review'])
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch case tags')
      setAvailableTags(['urgent', 'follow-up', 'reviewed', 'needs-review'])
    } finally {
      setLoading(false)
    }
  }, [apiClasses?.CaseTagsAPI])

  const handleTagChange = useCallback(async ({ added, removed, allTags }) => {
    if (!apiClasses?.CaseTagsAPI) {
      return
    }

    try {
      if (added.length > 0) {
        for (const tag of added) {
          await apiClasses.CaseTagsAPI.addCaseTag({ caseId, tag })
        }
        message.success(`Added ${added.length} tag(s)`)
      }

      if (removed.length > 0) {
        for (const tag of removed) {
          await apiClasses.CaseTagsAPI.removeCaseTag({ caseId, tag })
        }
        message.success(`Removed ${removed.length} tag(s)`)
      }

      if (onTagsUpdated && (added.length > 0 || removed.length > 0)) {
        onTagsUpdated(allTags)
      }
    } catch (err) {
      message.error('Error updating tags')
    }
  }, [caseId, apiClasses?.CaseTagsAPI, onTagsUpdated])

  useEffect(() => {
    fetchAvailableTags()
  }, [fetchAvailableTags])

  return {
    availableTags,
    loading,
    error,
    handleTagChange,
    refetch: fetchAvailableTags
  }
}
